/*
 * File: DeploymentRequest.ts
 * Project: rosita
 * -----
 * Last Modified: 21 Feb 2022 1:13:30 am
 * -----
 * Copyright (c) 2022 Elegant Software Solutions All rights reserved.
 */

import { RcFile } from 'antd/lib/upload/interface';
import { Address } from 'redux/services/chuckieSue/models/addresses';
import * as yup from 'yup';
import { Address as DeploymentRequestAddress, addressSchema } from './Address';
import { ProductPayload, productSchema } from './Product';
import { Project, projectSchema } from './Project';

export interface DeploymentRequest {
  attachments: RcFile[];
  business: BusinessReference;
  contactEmail: string;
  contactName: string;
  contactPhone: string;
  createdByFullName: string;
  createdByUserId: string;
  createdDateTime: string;
  customerReferenceNumber: string | null;
  customerPurchaseOrderNumber: string | null;
  deploymentRequestTypeId: string;
  division: DivisionReference;
  // TODO: Turn into a ENUM
  documentType: string;
  id: string;
  installAddress: DeploymentRequestAddress | null;
  isActive: boolean;
  isDeleted: boolean;
  isOrderReleased: boolean;
  isExpediteRequested: boolean;
  isShipCompleteRequired: boolean;
  isLabConfigurationRequired: boolean;
  modifiedByFullName: string;
  modifiedByUserId: string;
  modifiedDateTime: string;
  note: string | null;
  notificationEmails: string[];
  partitionKey: string;
  partitionKeyDescription: string;
  lineItems: ProductPayload[];
  // TODO: Remove undefined on project name when backend support made
  project?: Project | null;
  requestedDeliveryDate: string | null;
  requestNumber: string;
  salesOrderNumber: string | null;
  shippingAddress: DeploymentRequestAddress | null;
  status: DeploymentStatusesLabels;
  transactionType: string;
  // NOT SURE WHAT THESE ARE FOR
  _attachments?: string;
  _etag?: string;
  _rid?: string;
  _self?: string;
  _ts?: number;
}

export interface BusinessReference {
  dataAreaId: string;
  erpId: string;
  id: string;
}

export interface DivisionReference {
  id: string;
  erpId: string;
  name: string;
}

export enum DeploymentStatuses {
  cancelled = 'cancelled',
  delivered = 'delivered',
  draft = 'draft',
  invoiced = 'invoiced',
  myOrders = 'myOrders',
  orderCreated = 'orderCreated',
  pendingApproval = 'pendingApproval',
  pendingTierTwoApproval = 'pendingTierTwoApproval',
  rejected = 'rejected',
  rejectedTierTwoApproval = 'rejectedTierTwoApproval',
  shipped = 'shipped',
  submitted = 'submitted',
  posted = 'posted'
}

export enum DeploymentStatusesLabels {
  cancelled = 'Cancelled',
  delivered = 'Delivered',
  draft = 'Draft',
  invoiced = 'Invoiced',
  myOrders = 'MyOrders',
  orderCreated = 'OrderCreated',
  pendingApproval = 'PendingApproval',
  pendingTierTwoApproval = 'PendingTierTwoApproval',
  rejected = 'Rejected',
  rejectedTierTwoApproval = 'RejectedTierTwoApproval',
  shipped = 'Shipped',
  submitted = 'Submitted',
  posted = 'Posted'
}

export interface DeploymentRequestParams {
  offset: number;
  requesterNameContains?: string;
  customerReferenceNumberContains?: string;
  salesOrderNumberContains?: string;
  customerPurchaseOrderNumberContains?: string;
  requestNumberContains?: string;
  startDate?: string;
  endDate?: string;
  statuses?: string;
  includeInactiveData?: boolean;
  includeDeletedData?: boolean;
  returnDeletedDataOnly?: boolean;
  deploymentRequestType?: string;
}

export enum DeploymentRequestsModes {
  view = 'view',
  recover = 'recover'
}

export interface DeploymentRequestPayload {
  id?: string;
  // Request Details
  requestNumber?: string;
  requestNumberSuffix?: string;
  customerReferenceNumber?: string;
  deploymentRequestTypeId?: string | null;
  projectId?: string | null;
  customerPurchaseOrderNumber?: string;
  // Install Location
  installAddressId?: string | null;
  // Products
  lineItems: ProductPayload[];
  // Shipping Location
  shippingAddressId?: string | null;
  // Shipping Details
  contactEmail?: string;
  contactName?: string;
  contactPhone?: string;
  requestedDeliveryDate: string;
  // More Details
  notificationEmails: string[];
  note?: string;

  project: Project | null;
  // Status
  status: DeploymentStatuses;
  // Details
  isOrderReleased: boolean;
  isLabConfigurationRequired: boolean;
  isShipCompleteRequired: boolean;
  // Attachments
  attachments: RcFile[];
  // Full Address Objects
  fullInstallAddress?: Address | null;
  fullShippingAddress?: Address | null;
  // For Earliest Shipping Date Togle
  isExpediteRequested?: boolean;
}

export interface EarliestRequestDeliveryDateParams {
  deploymentRequestTypeId: string;
  destinationCountryCode: string;
  destinationSubdivisionCode: string;
  destinationPostalCode: string;
}

export enum DeploymentRequestLabels {
  // Request Details
  requestNumber = 'Parent Sparing ID',
  customerReferenceNumber = 'Primary Referenece Number',
  customerPurchaseOrderNumber = 'Secondary Reference Number',
  requestRuleType = 'Request Type',
  // Install Location
  installAddress = 'Outage Location',
  // Products
  products = 'Products',
  // Shipping Location
  shippingAddress = 'Shipping Location',
  // Shipping Details
  contactEmail = 'Email',
  contactName = 'Name',
  contactPhone = 'Phone Number',
  requestedDeliveryDate = 'Requested Delivery Date',
  // More Details
  notificationEmails = 'Notification Subscriptions',
  note = 'Notes',

  project = 'Project Name',
  status = 'Status',
  isOrderReleased = 'Order Released',
  isLabConfigurationRequired = 'Lab Configuration Required',
  isShipCompleteRequired = 'Ship Complete Required'
}

export const deploymentRequestPayloadSchema: yup.SchemaOf<DeploymentRequestPayload> = yup.object({
  id: yup.string(),
  requestNumber: yup.string().label(DeploymentRequestLabels.requestNumber),
  requestNumberSuffix: yup.string().label(DeploymentRequestLabels.requestNumber + ' Suffix'),
  customerReferenceNumber: yup.string().label(DeploymentRequestLabels.customerReferenceNumber),
  customerPurchaseOrderNumber: yup.string().label(DeploymentRequestLabels.customerPurchaseOrderNumber),
  deploymentRequestTypeId: yup.string().label('Config ID').nullable(),
  projectId: yup.string().label('Project ID').nullable(),
  installAddressId: addressSchema.required(),
  lineItems: yup.array(productSchema),
  shippingAddressId: addressSchema.required(),
  contactEmail: yup.string().label(DeploymentRequestLabels.contactEmail),
  contactName: yup.string().label(DeploymentRequestLabels.contactName),
  contactPhone: yup
    .string()

    .label(DeploymentRequestLabels.contactPhone)
    .test('phone-format', 'Phone number must be exactly 10 digits', (value) => {
      if (!value) return true; // Allow empty for optional fields

      const digitsOnly = value.replace(/\D/g, '');

      return digitsOnly.length === 10;
    })
    .test('phone-required-for-waybills', 'Phone number is required for waybill generation', (value, context) => {
      // If there are defective returns, phone number is required
      const hasDefectiveReturns = context.parent.defectiveReturns?.items?.length > 0;

      if (hasDefectiveReturns && !value) {
        return false;
      }

      return true;
    }),
  requestedDeliveryDate: yup.string().label(DeploymentRequestLabels.requestedDeliveryDate).required(),
  notificationEmails: yup.array(yup.string().required().email()).default([]),
  note: yup.string(),
  project: projectSchema.nullable(),
  status: yup.mixed().oneOf(Object.values(DeploymentStatuses)).required().default(DeploymentStatuses.draft),
  isOrderReleased: yup.boolean().required().default(false),
  isShipCompleteRequired: yup.boolean().required().default(false),
  isLabConfigurationRequired: yup.boolean().required().default(false),
  attachments: yup.array(),
  fullInstallAddress: addressSchema.nullable(),
  fullShippingAddress: addressSchema.nullable(),
  isExpediteRequested: yup.boolean().required().default(false)
});
